import { z } from 'zod';
import validator from 'validator';

// Common XSS-safe validation patterns
export const XSS_SAFE_PATTERNS = {
  // Basic text with no HTML tags
  SAFE_TEXT: /^[a-zA-Z0-9\s\-_.,!?()\[\]'"@#$%&*+=:;]*$/,
  // Alphanumeric with spaces, dashes, underscores
  ALPHANUMERIC_PLUS: /^[a-zA-Z0-9\s\-_]*$/,
  // Email validation (using validator library)
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // URL validation
  URL: /^https?:\/\/[^\s<>"{}|\\^`\[\]]*$/,
  // Phone number (basic international format)
  PHONE: /^\+?[\d\s\-()]*$/,
  // Organization/company names
  ORGANIZATION_NAME: /^[a-zA-Z0-9\s\-_.,&'()]*$/,
  // File names
  FILENAME: /^[a-zA-Z0-9\-_. ]*$/,
  // IP addresses
  IP_ADDRESS: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
};

// XSS protection functions
export function sanitizeText(input: string): string {
  if (typeof input !== 'string') return '';
  
  // First decode any existing HTML entities to prevent double-encoding
  const decoded = input
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'");
  
  // Remove HTML tags and encode dangerous characters
  // Note: We don't encode & or ' because they're safe in database storage.
  // Single quotes are safely handled by React's JSX escaping on render.
  return decoded
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>"]/g, (match) => {
      const htmlEntities: Record<string, string> = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
      };
      return htmlEntities[match] || match;
    })
    .trim();
}

export function sanitizeUrl(input: string): string {
  if (typeof input !== 'string') return '';
  
  // Only allow http/https URLs
  if (!validator.isURL(input, { protocols: ['http', 'https'] })) {
    return '';
  }
  
  return input.trim();
}

export function sanitizeEmail(input: string): string {
  if (typeof input !== 'string') return '';
  
  const email = input.toLowerCase().trim();
  return validator.isEmail(email) ? email : '';
}

export function sanitizeFilename(input: string): string {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(/[^a-zA-Z0-9\-_. ]/g, '') // Remove dangerous characters
    .replace(/\.{2,}/g, '.') // Prevent directory traversal
    .trim();
}

// Enhanced Zod schemas with XSS protection
export const xssSafeString = (maxLength: number = 255) =>
  z.string()
    .max(maxLength, `Text must be less than ${maxLength} characters`)
    .transform(sanitizeText)
    .refine((val) => val.length > 0, 'Field cannot be empty after sanitization')
    .refine((val) => !/<script|javascript:|data:/i.test(val), 'Potentially dangerous content detected');

export const xssSafeEmail = () =>
  z.string()
    .email('Invalid email format')
    .transform(sanitizeEmail)
    .refine((val) => val.length > 0, 'Invalid email after sanitization');

export const xssSafeUrl = () =>
  z.string()
    .url('Invalid URL format')
    .transform(sanitizeUrl)
    .refine((val) => val.length > 0, 'Invalid URL after sanitization');

export const xssSafeFilename = () =>
  z.string()
    .max(255, 'Filename too long')
    .transform(sanitizeFilename)
    .refine((val) => val.length > 0, 'Invalid filename after sanitization');

export const xssSafeTextarea = (maxLength: number = 2000) =>
  z.string()
    .max(maxLength, `Text must be less than ${maxLength} characters`)
    .transform((val) => {
      // First decode any existing HTML entities to prevent double-encoding
      const decoded = val
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#x27;/g, "'")
        .replace(/&#39;/g, "'");
      
      // Allow line breaks but sanitize HTML
      // Note: We don't encode & or ' because they're safe in database storage.
      // Single quotes are safely handled by React's JSX escaping on render.
      return decoded
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/[<>"]/g, (match) => {
          const htmlEntities: Record<string, string> = {
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
          };
          return htmlEntities[match] || match;
        });
    })
    .refine((val) => !/<script|javascript:|data:/i.test(val), 'Potentially dangerous content detected');

// Enhanced organization name validation
export const xssSafeOrganizationName = () =>
  z.string()
    .min(1, 'Organization name is required')
    .max(100, 'Organization name must be less than 100 characters')
    .regex(XSS_SAFE_PATTERNS.ORGANIZATION_NAME, 'Organization name contains invalid characters')
    .transform(sanitizeText);

// Enhanced user name validation
export const xssSafeUserName = () =>
  z.string()
    .min(1, 'Name is required')
    .max(50, 'Name must be less than 50 characters')
    .regex(/^[a-zA-Z\s\-']*$/, 'Name contains invalid characters')
    .transform(sanitizeText);

// Phone number validation
export const xssSafePhone = () =>
  z.string()
    .optional()
    .transform((val) => val ? val.replace(/[^\d\s\-+()]/g, '') : val)
    .refine((val) => !val || XSS_SAFE_PATTERNS.PHONE.test(val), 'Invalid phone number format');

// Custom field validation for specific use cases
export const xssSafeCustomField = (pattern: RegExp, errorMessage: string, maxLength: number = 255) =>
  z.string()
    .max(maxLength, `Field must be less than ${maxLength} characters`)
    .regex(pattern, errorMessage)
    .transform(sanitizeText);

// Security validation helpers
export function validateInput(input: unknown, schema: z.ZodSchema): { success: boolean; data?: any; error?: string } {
  try {
    const result = schema.parse(input);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { success: false, error: error.errors[0]?.message || 'Validation failed' };
    }
    return { success: false, error: 'Unknown validation error' };
  }
}

// XSS detection function for backend middleware
export function detectXSS(input: string): boolean {
  if (typeof input !== 'string') return false;
  
  const xssPatterns = [
    /<script[\s\S]*?<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe[\s\S]*?<\/iframe>/gi,
    /<object[\s\S]*?<\/object>/gi,
    /<embed[\s\S]*?>/gi,
    /<form[\s\S]*?<\/form>/gi,
    /data:text\/html/gi,
    /vbscript:/gi,
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
}

// Content Security Policy helpers
export const CSP_DIRECTIVES = {
  'default-src': "'self'",
  'script-src': "'self' 'unsafe-inline' 'unsafe-eval' https://cdn.jsdelivr.net https://replit.com https://widget.intercom.io https://js.intercomcdn.com https://www.google.com https://www.gstatic.com https://sdk.canny.io https://*.canny.io",
  'style-src': "'self' 'unsafe-inline' https://fonts.googleapis.com https://*.canny.io",
  'font-src': "'self' https://fonts.gstatic.com https://*.intercomcdn.com",
  'img-src': "'self' data: https: blob:",
  'connect-src': "'self' https: wss: https://*.intercom.io https://*.intercomcdn.com wss://*.intercom.io https://www.google.com https://*.canny.io https://sdk.canny.io",
  'frame-src': "'self' https://*.intercom.io https://www.google.com https://www.gstatic.com https://*.canny.io",
  'frame-ancestors': "'none'",
  'base-uri': "'self'",
  'form-action': "'self'",
};

export function generateCSPHeader(): string {
  return Object.entries(CSP_DIRECTIVES)
    .map(([directive, value]) => `${directive} ${value}`)
    .join('; ');
}